import React, { useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Shield, FileText, Users, Activity, LogOut, Menu, X, BarChart3, Home, HelpCircle, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Login from './Login';
import StatsView from './StatsView';
import SubmissionsView from './SubmissionsView';
import UsersView from './UsersView';
import AuditView from './AuditView';
import AnalyticsView from './AnalyticsView';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: <Home size={22} />, label: 'HOME', hint: 'Your main screen — greeting, new alerts, quick overview', exact: true },
    { path: '/dashboard/submissions', icon: <FileText size={22} />, label: 'SUBMISSIONS', hint: 'View, search, and print all forms people submitted', badge: true },
    ...(user?.role === 'manager' ? [
      { path: '/dashboard/analytics', icon: <BarChart3 size={22} />, label: 'ANALYTICS', hint: 'Your business report — charges, areas, demographics' },
      { path: '/dashboard/users', icon: <Users size={22} />, label: 'USERS', hint: 'Add or remove people who can access this dashboard' },
      { path: '/dashboard/audit', icon: <Activity size={22} />, label: 'AUDIT LOG', hint: 'See every action taken in this system (who did what, when)' },
    ] : []),
  ];

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className={`dash-sidebar ${sidebarOpen ? 'dash-sidebar--open' : ''}`}>
        <div className="dash-sidebar__header">
          <div className="dash-sidebar__logo">LONE STAR</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>LICENSE #74629</div>
          <div style={{ marginTop: '0.75rem' }}>
            <span className={`dash-sidebar__role dash-sidebar__role--${user?.role || 'bondsman'}`}>
              {user?.role === 'manager' ? 'MANAGER' : 'BONDSMAN'}
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>{user?.name || user?.email}</div>
        </div>

        <nav className="dash-nav">
          {navItems.map(item => (
            <Link key={item.path} to={item.path}
              className={`dash-nav__item ${isActive(item.path, item.exact) ? 'dash-nav__item--active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              title={item.hint}>
              {item.icon}
              <div style={{ flex: 1 }}>
                <span>{item.label}</span>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', lineHeight: 1.3, marginTop: '0.15rem', opacity: 0.8 }}>{item.hint}</div>
              </div>
              {item.badge && <span className="dash-nav__badge" data-testid="new-submissions-badge">!</span>}
            </Link>
          ))}
        </nav>

        {/* Switch to Amigos Dashboard */}
        {user?.role === 'manager' && (
          <div style={{ padding: '0.75rem 1.25rem' }}>
            <a
              href="https://amigosbailbonds.com/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="switch-amigos-btn"
              onClick={() => setSidebarOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.85rem 1.1rem',
                background: 'rgba(243, 156, 18, 0.08)',
                border: '1px solid rgba(243, 156, 18, 0.30)',
                color: '#F39C12',
                textDecoration: 'none',
                fontFamily: 'var(--font-display)',
                fontSize: '0.82rem',
                letterSpacing: '0.06em',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(243, 156, 18, 0.15)'; e.currentTarget.style.borderColor = 'rgba(243, 156, 18, 0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(243, 156, 18, 0.08)'; e.currentTarget.style.borderColor = 'rgba(243, 156, 18, 0.30)'; }}
            >
              <ArrowRightLeft size={18} />
              <div style={{ flex: 1 }}>
                <div>AMIGO'S DASHBOARD</div>
                <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', marginTop: '0.15rem', letterSpacing: '0.03em', fontFamily: 'var(--font-body)' }}>
                  Switch to Amigo's Bail Bonds
                </div>
              </div>
            </a>
          </div>
        )}

        {/* Help section */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-dim)', background: 'rgba(235, 110, 31, 0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <HelpCircle size={14} style={{ color: 'var(--astros-orange)' }} />
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Need help?</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '0.75rem' }}>
            Call James at Texas IT Nerds for any tech questions.
          </div>
        </div>

        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-dim)' }}>
          <button onClick={logout} className="btn-outline" style={{ width: '100%', justifyContent: 'center', gap: '0.75rem' }}
            data-testid="logout-btn">
            <LogOut size={18} /> LOGOUT
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dash-content">
        {/* Mobile Header */}
        <div className="dash-mobile-header" data-testid="dash-mobile-header">
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className="dash-mobile-toggle"
            data-testid="dash-mobile-toggle"
            aria-label="Toggle sidebar">
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <span className="dash-mobile-title">LONE STAR BONDING</span>
        </div>
        {children}
      </main>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999 }} onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

const DashboardApp = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={{ minHeight: '100vh', background: 'var(--void)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading...</div>;
  }

  if (!user) return <Login />;

  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<StatsView />} />
        <Route path="/submissions" element={<SubmissionsView />} />
        {user.role === 'manager' && <Route path="/analytics" element={<AnalyticsView />} />}
        {user.role === 'manager' && <Route path="/users" element={<UsersView />} />}
        {user.role === 'manager' && <Route path="/audit" element={<AuditView />} />}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardLayout>
  );
};

export default DashboardApp;
